import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { DocumentTemplateService } from './document-template.service';
import { AnchorTreeService } from './anchor-tree.service';
import { OcrPrimeStore } from '../store/ocr-prime.store';
import { DocumentTemplateWithPath } from '../types/document-template';
import { AnchorTreeLeafNode, AnchorTreeNode } from '../types/anchor-tree';
import {
  findLeaf,
  findLeafForTemplate,
  getExpectedFieldIdsForLeaf,
  getPathBranchExpectations,
  getPathDecisiveFieldIds
} from '../utils/anchor-tree.utils';
import {
  AnchorReviewDialogComponent,
  AnchorReviewDialogData,
  AnchorReviewDialogResult,
  AnchorReviewData,
  AnchorReviewRow,
  PathWarning
} from '../pages/templates/components/anchor-review-dialog/anchor-review-dialog.component';

/**
 * Shared launcher for the template→leaf assignment / re-extraction flow:
 *
 * 1. Derive the expected anchor set (path decisive ∪ leaf anchors) for the leaf.
 * 2. Open the read-only review dialog immediately; the dialog shows a loading
 *    indicator while the template PDF is fetched and exactly those anchors are
 *    extracted (scoped). Re-extract is allowed, manual edit is not (Q8 path
 *    warnings shown).
 * 3. On confirm: update the tree membership (source of truth) AND the template
 *    (`anchors` replaced + `leafNodeId`). Nothing is saved without confirmation.
 */
@Injectable({ providedIn: 'root' })
export class TemplateAssignmentService {
  private readonly templateService = inject(DocumentTemplateService);
  private readonly anchorTreeService = inject(AnchorTreeService);
  private readonly store = inject(OcrPrimeStore);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);

  /**
   * Runs the full assignment flow for a template onto a target leaf.
   * Resolves to true when the assignment was confirmed and saved.
   */
  async assignTemplateToLeaf(
    template: DocumentTemplateWithPath,
    targetLeafNodeId: string
  ): Promise<boolean> {
    const root = this.store.anchorTree()?.root ?? null;
    const leaf = findLeaf(root, targetLeafNodeId);
    if (!leaf) {
      this.snackBar.open('Target leaf no longer exists — refresh the tree.', 'Close', { duration: 4000 });
      return false;
    }

    const result = await this.openReviewDialog(template, root, leaf);
    if (!result?.confirmed) {
      return false;
    }

    try {
      await this.persistAssignment(template, root, targetLeafNodeId, result.anchors);
      this.snackBar.open(`Template "${template.name}" assigned to "${this.leafLabel(leaf)}".`, 'Close', { duration: 3000 });
      return true;
    } catch (err) {
      console.error('Failed to persist assignment:', err);
      this.snackBar.open('Failed to save the assignment. Reloading data…', 'Close', { duration: 4000 });
      this.store.refreshAnchorTree();
      this.store.refreshTemplates();
      return false;
    }
  }

  /**
   * Re-extraction flow for a template's CURRENT leaf (badge/banner deep-link).
   */
  async reextractForCurrentLeaf(template: DocumentTemplateWithPath): Promise<boolean> {
    const root = this.store.anchorTree()?.root ?? null;
    const leaf = template.leafNodeId
      ? findLeaf(root, template.leafNodeId)
      : findLeafForTemplate(root, template.id);
    if (!leaf) {
      this.snackBar.open(
        'This template is not assigned to any anchor-tree leaf. Assign it on the Templates page first.',
        'Close',
        { duration: 5000 }
      );
      return false;
    }
    return this.assignTemplateToLeaf(template, leaf.id);
  }

  /**
   * Unassigns a template from its leaf: clears tree membership, `leafNodeId`
   * and its (leaf-scoped, now meaningless) anchors.
   */
  async unassignTemplate(template: DocumentTemplateWithPath): Promise<boolean> {
    const root = this.store.anchorTree()?.root ?? null;
    try {
      const newRoot = this.removeTemplateFromTree(root, template.id);
      await firstValueFrom(this.anchorTreeService.saveAnchorTree(newRoot));
      await firstValueFrom(
        this.templateService.updateDocumentTemplate(template.id, {
          name: template.name,
          description: template.description,
          structure: template.structure,
          fieldsStructureId: template.fieldsStructureId,
          autoBlocked: template.autoBlocked,
          anchors: {},
          leafNodeId: null as unknown as undefined
        })
      );
      this.store.refreshAnchorTree();
      this.store.refreshTemplates();
      this.snackBar.open(`Template "${template.name}" unassigned — it is now unreachable for matching.`, 'Close', { duration: 4000 });
      return true;
    } catch (err) {
      console.error('Failed to unassign template:', err);
      this.snackBar.open('Failed to unassign the template.', 'Close', { duration: 4000 });
      this.store.refreshAnchorTree();
      this.store.refreshTemplates();
      return false;
    }
  }

  // ── internals ───────────────────────────────────────────────────────────

  private async extractForLeaf(
    template: DocumentTemplateWithPath,
    root: AnchorTreeNode | null,
    leafNodeId: string
  ): Promise<{ anchors: Record<string, unknown> }> {
    const expectedFieldIds = getExpectedFieldIdsForLeaf(root, leafNodeId);
    const response = await firstValueFrom(
      this.templateService.extractAnchors(template.id, expectedFieldIds)
    );
    return { anchors: response.anchors ?? {} };
  }

  private async openReviewDialog(
    template: DocumentTemplateWithPath,
    root: AnchorTreeNode | null,
    leaf: AnchorTreeLeafNode
  ): Promise<AnchorReviewDialogResult | undefined> {
    const fieldsById = this.store.fieldsByIdMap();
    const pathFieldIds = getPathDecisiveFieldIds(root, leaf.id);
    const leafFieldIds = (leaf.leafAnchorFieldIds ?? []).filter(id => !pathFieldIds.includes(id));

    const buildReview = (anchors: Record<string, unknown>): AnchorReviewData => {
      const toRows = (fieldIds: string[], kind: AnchorReviewRow['kind']): AnchorReviewRow[] =>
        fieldIds
          .map(id => fieldsById.get(id))
          .filter((f): f is NonNullable<typeof f> => !!f)
          .map(field => ({ kind, fieldName: field.name, value: anchors[field.name] }));

      // Q8: warn (non-blocking) when an extracted path value contradicts the
      // branch expectation that routes to this leaf.
      const warnings: PathWarning[] = [];
      for (const expectation of getPathBranchExpectations(root, leaf.id)) {
        const field = fieldsById.get(expectation.fieldId);
        if (!field) continue;
        const extracted = anchors[field.name];
        if (extracted === undefined || extracted === null || extracted === '') continue;
        if (this.normalize(String(extracted)) !== this.normalize(expectation.expectedValue)) {
          warnings.push({
            fieldName: field.name,
            extractedValue: String(extracted),
            expectedValue: expectation.expectedValue
          });
        }
      }

      return {
        pathRows: toRows(pathFieldIds, 'path'),
        leafRows: toRows(leafFieldIds, 'leaf'),
        warnings
      };
    };

    // Open the dialog immediately — the dialog shows a loading indicator while
    // the (potentially slow) anchor extraction runs.
    const dialogRef = this.dialog.open<AnchorReviewDialogComponent, AnchorReviewDialogData, AnchorReviewDialogResult>(
      AnchorReviewDialogComponent,
      {
        width: '560px',
        disableClose: true,
        data: {
          templateName: template.name,
          leafLabel: this.leafLabel(leaf),
          extract: async () => {
            const extraction = await this.extractForLeaf(template, root, leaf.id);
            return extraction.anchors;
          },
          buildReview
        }
      }
    );

    return firstValueFrom(dialogRef.afterClosed());
  }

  private async persistAssignment(
    template: DocumentTemplateWithPath,
    root: AnchorTreeNode | null,
    targetLeafNodeId: string,
    anchors: Record<string, unknown>
  ): Promise<void> {
    // 1. Tree membership first (source of truth): remove from any leaf, add to target.
    const withoutTemplate = this.removeTemplateFromTree(root, template.id);
    const newRoot = this.addTemplateToLeaf(withoutTemplate, targetLeafNodeId, template.id);
    await firstValueFrom(this.anchorTreeService.saveAnchorTree(newRoot));

    // 2. Template: replace anchors map (Q5-A) + back-reference.
    await firstValueFrom(
      this.templateService.updateDocumentTemplate(template.id, {
        name: template.name,
        description: template.description,
        structure: template.structure,
        fieldsStructureId: template.fieldsStructureId,
        autoBlocked: template.autoBlocked,
        anchors,
        leafNodeId: targetLeafNodeId
      })
    );

    this.store.refreshAnchorTree();
    this.store.refreshTemplates();
  }

  private removeTemplateFromTree(root: AnchorTreeNode | null, templateId: string): AnchorTreeNode | null {
    if (!root) return null;
    const clone: AnchorTreeNode = JSON.parse(JSON.stringify(root));
    const visit = (node: AnchorTreeNode): void => {
      if (node.kind === 'leaf') {
        node.templateIds = (node.templateIds ?? []).filter(id => id !== templateId);
        return;
      }
      for (const branch of node.branches ?? []) {
        visit(branch.child);
      }
    };
    visit(clone);
    return clone;
  }

  private addTemplateToLeaf(
    root: AnchorTreeNode | null,
    leafNodeId: string,
    templateId: string
  ): AnchorTreeNode | null {
    if (!root) return null;
    const clone: AnchorTreeNode = JSON.parse(JSON.stringify(root));
    const leaf = findLeaf(clone, leafNodeId);
    if (leaf && !(leaf.templateIds ?? []).includes(templateId)) {
      leaf.templateIds = [...(leaf.templateIds ?? []), templateId];
    }
    return clone;
  }

  private leafLabel(leaf: AnchorTreeLeafNode): string {
    return leaf.label || leaf.id;
  }

  private normalize(value: string): string {
    return value
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
}

